import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, authToken } from "@/lib/api";
import { getTranslator, TRANSLATIONS } from "@/i18n";
import { applyTheme } from "@/themes";
import { setAutoTranslateLanguage } from "@/lib/i18nAuto";
import { BTC_ENABLED } from "@/lib/flags";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// Supported display cryptos. symbol → shown as prefix; else ticker shown as suffix.
export const CRYPTOS = {
  BTC: { symbol: "₿", digits: 6 },
  ETH: { symbol: "Ξ", digits: 5 },
  USDT: { label: "USDT", digits: 2 },
  USDC: { label: "USDC", digits: 2 },
  SOL: { label: "SOL", digits: 3 },
  BNB: { label: "BNB", digits: 4 },
  XRP: { label: "XRP", digits: 2 },
};

// Convert an ISO country code (e.g. "NP") to its flag emoji
export function flagFromCode(code) {
  if (!code || code.length !== 2) return "🌐";
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

const LANG_KEY = "rudram_lang";
const THEME_KEY = "rudram_theme";
const BG_KEY = "rudram_bg";
const CUR_KEY = "rudram_currency";
const COUNTRY_KEY = "rudram_country";
const FONT_KEY = "rudram_font";

export const FONTS = {
  manrope: { label: "Warm", stack: "'Manrope', sans-serif", primary: true },
  jakarta: { label: "Friendly", stack: "'Plus Jakarta Sans', sans-serif", primary: true },
  outfit: { label: "Modern", stack: "'Outfit', sans-serif", primary: true },
  grotesk: { label: "Distinctive", stack: "'Space Grotesk', sans-serif" },
  mono: { label: "Technical", stack: "'JetBrains Mono', monospace" },
};

function detectLang() {
  const saved = localStorage.getItem(LANG_KEY);
  if (saved) return saved;
  const nav = (navigator.language || "en").slice(0, 2);
  return TRANSLATIONS[nav] ? nav : "en";
}

function initTheme() {
  const stored = localStorage.getItem(THEME_KEY);
  // One-time migration off the old B&W default → colorful galaxy.
  if (!localStorage.getItem("rudram_theme_colorful")) {
    localStorage.setItem("rudram_theme_colorful", "1");
    if (!stored || stored === "mono") { localStorage.setItem(THEME_KEY, "cosmic"); return "cosmic"; }
  }
  return stored || "cosmic";
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking
  const [meta, setMeta] = useState(null);
  const [lang, setLangState] = useState(detectLang());
  const [theme, setThemeState] = useState(initTheme());
  const [bgImage, setBgImageState] = useState(localStorage.getItem(BG_KEY) || "");
  const [currency, setCurrencyState] = useState(localStorage.getItem(CUR_KEY) || "");
  const [country, setCountryState] = useState(() => {
    // One-time migration: drop a stale auto-detected country (old builds set "US" by IP)
    // so Bharat 🇮🇳 becomes the effective default. User-chosen countries after this persist.
    try {
      if (!localStorage.getItem("mykind_country_migrated_v2")) {
        localStorage.removeItem(COUNTRY_KEY);
        localStorage.setItem("mykind_country_migrated_v2", "1");
      }
    } catch (_) {}
    return localStorage.getItem(COUNTRY_KEY) || "IN";
  });

  // Capture an affiliate ?ref= on ANY page so members earn from any link they share.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("ref");
      const code = (p || "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40);
      if (code) {
        localStorage.setItem("mykind_ref", code);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/click`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        }).catch(() => {});
      }
    } catch (_) {}
  }, []);
  const [font, setFontState] = useState(localStorage.getItem(FONT_KEY) || "manrope");

  const t = getTranslator(lang);
  const rtl = ["ar", "ur", "ks", "sd"].includes(lang);

  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    const f = FONTS[font] || FONTS.manrope;
    document.documentElement.style.setProperty("--rud-font-body", f.stack);
  }, [font]);
  useEffect(() => {
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    setAutoTranslateLanguage(lang);
  }, [lang, rtl]);

  const setLang = (l) => { localStorage.setItem(LANG_KEY, l); setLangState(l); };
  const setTheme = (t) => { localStorage.setItem(THEME_KEY, t); setThemeState(t); };
  const setBgImage = (b) => { localStorage.setItem(BG_KEY, b || ""); setBgImageState(b || ""); };
  const persistCurrency = (c) => { localStorage.setItem(CUR_KEY, c); setCurrencyState(c); };
  // Explicit user choice (from a currency picker) — remembered so we don't override it.
  const setCurrency = (c) => { localStorage.setItem("rudram_currency_set", "1"); persistCurrency(c); };
  const setFont = (f) => { localStorage.setItem(FONT_KEY, f); setFontState(f); };
  const setCountry = (code) => {
    localStorage.setItem(COUNTRY_KEY, code);
    setCountryState(code);
  };
  const countryObj = (meta?.countries || []).find((x) => x.code === country) || null;
  const cryptoPrices = meta?.crypto_prices || {};
  const btcPrice = cryptoPrices?.BTC?.usd || meta?.btc_price_usd || 65000;
  // Local-first: explicit pick > wallet users default BTC > the country's local currency. No USD default.
  const localCurrency = countryObj?.currency || "USD";
  // When Bitcoin is disabled, never treat a (stale) crypto pick as the active currency.
  const pickedCurrency = (!BTC_ENABLED && CRYPTOS[currency]) ? "" : currency;
  const activeCurrency = pickedCurrency || ((BTC_ENABLED && user?.auth_provider === "wallet") ? "BTC" : localCurrency);
  // Unified money formatter — $1 = 1 credit, rendered in the active display currency (local fiat or crypto).
  const fmtMoney = (usd) => {
    const n = Number(usd) || 0;
    const cur = activeCurrency;
    if (CRYPTOS[cur]) {
      const px = cryptoPrices?.[cur]?.usd;
      if (px) {
        const c = CRYPTOS[cur];
        const val = (n / px).toLocaleString(undefined, { maximumFractionDigits: c.digits });
        return c.symbol ? `${c.symbol}${val}` : `${val} ${c.label}`;
      }
    }
    const fx = meta?.currencies?.[cur];
    if (fx && cur !== "USD") return `${fx.symbol}${Math.round(n * fx.rate).toLocaleString()}`;
    return `$${Math.round(n).toLocaleString()}`;
  };
  // Ensure a country exists in meta (for global users whose country isn't pre-seeded)
  const ensureCountry = (obj) => {
    if (!obj?.code) return;
    setMeta((prev) => {
      const list = prev?.countries || [];
      if (list.some((x) => x.code === obj.code)) return prev;
      const merged = [...list, { region: "Global", currency: "USD", ...obj }]
        .sort((a, b) => a.name.localeCompare(b.name));
      return { ...(prev || {}), countries: merged };
    });
  };

  // Persist the member's country to the backend so the Adaptive Equity margin applies server-side.
  useEffect(() => {
    if (user && country) {
      api.post("/me/country", { country })
        .then((r) => {
          if (r.data?.search_cost !== undefined) {
            setUser((u) => (u ? { ...u, search_cost: r.data.search_cost, free_used: u.free_used } : u));
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, country]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      // Only a real auth rejection should log the user out. Network/aborted errors
      // (e.g. the service-worker controllerchange reload cancels the in-flight call)
      // must NOT wipe a valid token — retry once, then keep the token if still unclear.
      if (status === 401 || status === 403) {
        authToken.clear();
        setUser(null);
        return null;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return data;
      } catch (e2) {
        const s2 = e2?.response?.status;
        if (s2 === 401 || s2 === 403) { authToken.clear(); setUser(null); return null; }
        setUser(null); // token preserved; session may recover on next load
        return null;
      }
    }
  }, []);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (window.location.hash?.includes("session_id=")) {
      setUser(null); // AuthCallback will handle
    } else {
      refreshUser();
    }
  }, [refreshUser]);

  // Auto-detect country by location (locale first, then IP) unless user already chose one
  const COUNTRY_LANG = {
    CN: "zh", JP: "ja", RU: "ru", DE: "de", FR: "fr",
    ES: "es", MX: "es", AR: "es", CO: "es", CL: "es",
    BR: "pt", PT: "pt", IN: "hi",
    AE: "ar", SA: "ar", EG: "ar", QA: "ar",
  };
  // Default country is Bharat 🇮🇳 (set above). We intentionally do NOT auto-switch the
  // country by IP/locale on load — every signed-in member is asked to set their real
  // location via the mandatory LocationPrompt, which updates the country accurately.
  const applyCountry = (code) => {
    setCountry(code);
    if (!localStorage.getItem(LANG_KEY)) {
      const l = COUNTRY_LANG[code] || "en";
      if (TRANSLATIONS[l]) setLangState(l);
    }
  };

  const login = async (email, password, adminKey) => {
    const { data } = await api.post("/auth/login", { email, password, admin_key: adminKey || null });
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    authToken.clear();
    setUser(null);
  };

  return (
    <AppContext.Provider value={{
      user, setUser, meta, lang, setLang, t, rtl, theme, setTheme,
      bgImage, setBgImage, currency: activeCurrency, rawCurrency: currency, setCurrency, country, setCountry, countryObj, ensureCountry,
      font, setFont, fonts: FONTS, btcPrice, fmtMoney, cryptos: BTC_ENABLED ? CRYPTOS : {}, cryptoPrices, localCurrency,
      login, register, logout, refreshUser, apiError,
    }}>
      {children}
    </AppContext.Provider>
  );
}
